/** Context-help side panel for a selected schema property.
 *
 * Ported (behaviour-preserving) from the `HelpPane` in
 * `../AssistedConfigEditor.tsx`, re-parameterized to take `prop` + `path`
 * directly. A null `prop` renders the "not in schema" warning (matching the
 * original, where unknown rows carry `schema: null`).
 */
import { Alert, Box, Chip, Typography } from '@mui/material';

import { enumLabel, formatType } from './schemaOps';
import type { SchemaProp } from './types';

export interface HelpPaneProps {
  prop: SchemaProp | null;
  /** Dotted path of the selected row, e.g. "email.reply_to". */
  path: string;
}

export function HelpPane({ prop, path }: HelpPaneProps) {
  return (
    <Box sx={{ p: 2, height: '100%', overflowY: 'auto' }}>
      <Typography variant="subtitle2" sx={{ fontFamily: 'monospace', mb: 0.5 }}>
        {path}
      </Typography>
      <Chip label={formatType(prop)} size="small" sx={{ mb: 1.5 }} />

      {prop?.deprecated && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          This key is deprecated — prefer the more specific alternatives.
        </Alert>
      )}

      {prop?.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {prop.description}
        </Typography>
      )}

      {prop?.['x-unit'] && (
        <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
          <strong>Unit:</strong> {prop['x-unit']}
        </Typography>
      )}

      {prop?.default !== undefined && (
        <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
          <strong>Default:</strong>{' '}
          <Box component="span" sx={{ fontFamily: 'monospace' }}>
            {JSON.stringify(prop.default)}
          </Box>
        </Typography>
      )}

      {prop?.['x-default-source'] && (
        <Typography variant="caption" display="block" color="text.disabled" sx={{ mb: 1 }}>
          Source: {prop['x-default-source']}
        </Typography>
      )}

      {prop?.enum && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
            <strong>Allowed values:</strong>
          </Typography>
          {prop.enum.map((ev) => {
            const label = enumLabel(prop, ev);
            return (
              <Box key={String(ev)} sx={{ display: 'flex', gap: 1, mb: 0.25 }}>
                <Box
                  component="span"
                  sx={{ fontFamily: 'monospace', fontSize: 12, minWidth: 40 }}
                >
                  {JSON.stringify(ev)}
                </Box>
                {label && (
                  <Typography variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {prop?.['x-format-hint'] && (
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
          Format: {prop['x-format-hint']}
        </Typography>
      )}

      {prop?.['$comment'] && (
        <Typography variant="caption" display="block" color="text.disabled" sx={{ mt: 1 }}>
          {prop['$comment']}
        </Typography>
      )}

      {!prop && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          This key is not defined in the schema.
        </Alert>
      )}
    </Box>
  );
}
